import { useEffect, useRef, useCallback } from 'react'
import toast from 'react-hot-toast'
import { OnboardingProvider, useOnboarding } from '../../contexts/OnboardingContext'
import { StepNavigator } from './StepNavigator'
import { BusinessTypeStep } from './BusinessTypeStep'
import { VerificationStep } from './VerificationStep'
import { IndustryStep } from './IndustryStep'
import { UserDetailsStep } from './UserDetailsStep'

const TOTAL_STEPS = 4

/**
 * Inner wizard component that consumes OnboardingContext.
 * Handles step rendering, navigation, and session expiry detection.
 */
function OnboardingWizardInner() {
  const { state, dispatch } = useOnboarding()
  const { currentStep, completedSteps } = state

  // Track the previous step to detect unexpected resets (session expiry)
  const prevStepRef = useRef<number>(currentStep)

  useEffect(() => {
    const prevStep = prevStepRef.current
    // Detect session expiry: currentStep resets to 1 after having been > 1
    if (prevStep > 1 && currentStep === 1) {
      toast.error('Your session has expired. Please start again.', {
        duration: 5000,
        id: 'session-expired',
      })
    }
    prevStepRef.current = currentStep
  }, [currentStep])

  const handleNext = useCallback(() => {
    dispatch({ type: 'MARK_STEP_COMPLETE', payload: currentStep })
    if (currentStep < TOTAL_STEPS) {
      dispatch({ type: 'SET_STEP', payload: currentStep + 1 })
    }
  }, [currentStep, dispatch])

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      dispatch({ type: 'SET_STEP', payload: currentStep - 1 })
    }
  }, [currentStep, dispatch])

  const handleStepClick = useCallback(
    (step: number) => {
      // Only allow navigation to completed steps or the current step
      if (completedSteps.has(step) || step === currentStep) {
        dispatch({ type: 'SET_STEP', payload: step })
      }
    },
    [completedSteps, currentStep, dispatch]
  )

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return <BusinessTypeStep onNext={handleNext} />
      case 2:
        return <VerificationStep onNext={handleNext} onBack={handleBack} />
      case 3:
        return <IndustryStep onNext={handleNext} onBack={handleBack} />
      case 4:
        return <UserDetailsStep onBack={handleBack} />
      default:
        return <BusinessTypeStep onNext={handleNext} />
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center py-8 px-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <h1 className="text-2xl font-bold text-gray-900 text-center mb-6">
          Business Onboarding
        </h1>

        {/* Step Navigator */}
        <div className="mb-8">
          <StepNavigator
            currentStep={currentStep}
            completedSteps={completedSteps}
            totalSteps={TOTAL_STEPS}
            onStepClick={handleStepClick}
          />
        </div>

        {/* Current Step Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {renderCurrentStep()}
        </div>
      </div>
    </div>
  )
}

/**
 * OnboardingWizard root component.
 * Wraps the wizard steps in OnboardingProvider for shared state management.
 *
 * Requirements: 1.1, 5.5, 5.6, 5.7
 */
export function OnboardingWizard() {
  return (
    <OnboardingProvider>
      <OnboardingWizardInner />
    </OnboardingProvider>
  )
}
